/**
 * Sidebar do Flow Builder com blocos arrastáveis
 * Organiza blocos por categorias (mensagens, interação, lógica, ações, integrações)
 */

import {
  MessageSquare,
  HelpCircle,
  GitBranch,
  Clock,
  Tag,
  Tags,
  Webhook,
  Image,
  Grid,
  List,
  FormInput,
  User,
  UserPlus,
  UserCheck,
  Building,
  Play,
  Variable,
  CornerDownRight,
  Shuffle,
  FileText,
  Target,
  LayoutGrid,
  Briefcase,
  Plus,
  ArrowRightLeft,
  MinusCircle,
  Instagram,
  MessageSquareMore,
  CalendarClock,
  MousePointerClick,
  BarChart3,
  Code2,
  Bell,
  FileSignature,
  DollarSign,
  PhoneOff,
  ListTodo,
  Plug,
  ClipboardList,
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { BlockCategory, BlockDefinition } from '@/types/flows';

/**
 * Definição das categorias e blocos disponíveis
 */
const blockCategories: BlockCategory[] = [
  {
    name: 'Mensagens',
    icon: MessageSquare,
    blocks: [
      { 
        type: 'message_text', 
        label: 'Texto', 
        icon: MessageSquare, 
        color: 'bg-blue-500',
        description: 'Enviar mensagem de texto',
      },
      { 
        type: 'message_media', 
        label: 'Mídia', 
        icon: Image, 
        color: 'bg-purple-500',
        description: 'Enviar imagem, vídeo, áudio ou documento',
      },
      { 
        type: 'message_template', 
        label: 'Template', 
        icon: FileText, 
        color: 'bg-emerald-500',
        description: 'Enviar template do WhatsApp Oficial (HSM)',
      },
      { 
        type: 'message_carousel', 
        label: 'Carrossel', 
        icon: LayoutGrid, 
        color: 'bg-gradient-to-br from-amber-500 to-orange-500',
        description: 'Enviar múltiplos cartões com imagem, texto e botões',
      },
      { 
        type: 'send_button_link', 
        label: 'Botão Link', 
        icon: MousePointerClick, 
        color: 'bg-violet-500',
        description: 'Enviar mensagem com botão clicável contendo link (CTA)',
      },
      { 
        type: 'embed', 
        label: 'Incorporar', 
        icon: Code2, 
        color: 'bg-sky-500',
        description: 'Incorporar site, vídeo ou arquivo externo via iframe',
      },
    ],
  },
  {
    name: 'Interação',
    icon: HelpCircle,
    blocks: [
      { 
        type: 'question_buttons', 
        label: 'Botões', 
        icon: Grid, 
        color: 'bg-green-500',
        description: 'Pergunta com botões de escolha',
      },
      { 
        type: 'question_list', 
        label: 'Lista', 
        icon: List, 
        color: 'bg-green-600',
        description: 'Pergunta com lista de opções',
      },
      { 
        type: 'input_data', 
        label: 'Coletar Dado', 
        icon: FormInput, 
        color: 'bg-teal-500',
        description: 'Coletar informação do usuário',
      },
    ],
  },
  {
    name: 'Lógica',
    icon: GitBranch,
    blocks: [
      { 
        type: 'condition', 
        label: 'Condição', 
        icon: GitBranch, 
        color: 'bg-orange-500',
        description: 'Decidir caminho baseado em condições',
      },
      { 
        type: 'ab_test', 
        label: 'Teste A/B', 
        icon: Shuffle, 
        color: 'bg-pink-500',
        description: 'Distribuir fluxo por porcentagem',
      },
      { 
        type: 'delay', 
        label: 'Aguardar', 
        icon: Clock, 
        color: 'bg-yellow-500',
        description: 'Pausar execução por tempo determinado',
      },
      { 
        type: 'set_variable', 
        label: 'Definir Variável', 
        icon: Variable, 
        color: 'bg-cyan-500',
        description: 'Atribuir valor a uma variável',
      },
      { 
        type: 'filter_array', 
        label: 'Filtrar Array', 
        icon: List, 
        color: 'bg-amber-500',
        description: 'Filtrar itens de um array por condição',
      },
      { 
        type: 'format_array', 
        label: 'Formatar Array', 
        icon: FileText, 
        color: 'bg-emerald-500',
        description: 'Transformar array em texto formatado',
      },
      { 
        type: 'goto', 
        label: 'Pular', 
        icon: CornerDownRight, 
        color: 'bg-violet-500',
        description: 'Pular para outro node do fluxo',
      },
      { 
        type: 'trigger_check', 
        label: 'Verificar Gatilho', 
        icon: Target, 
        color: 'bg-amber-500',
        description: 'Ramificar baseado no gatilho que iniciou',
      },
      { 
        type: 'business_hours', 
        label: 'Horário Comercial', 
        icon: Clock, 
        color: 'bg-blue-600',
        description: 'Verificar se está no horário de atendimento',
      },
      {
        type: 'end_flow',
        label: 'Encerrar Fluxo',
        icon: PhoneOff,
        color: 'bg-destructive',
        description: 'Finaliza o fluxo (com despedida e fechamento de conversa opcionais)',
      },
    ],
  },
  {
    name: 'Ações',
    icon: Tag,
    blocks: [
      { 
        type: 'add_tag', 
        label: 'Adicionar Tag', 
        icon: Tag, 
        color: 'bg-pink-500',
        description: 'Adicionar tag ao contato',
      },
      { 
        type: 'remove_tag', 
        label: 'Remover Tag', 
        icon: MinusCircle, 
        color: 'bg-rose-500',
        description: 'Remover tag do contato ou negócio',
      },
      { 
        type: 'assign_phase', 
        label: 'Adicionar Fase', 
        icon: LayoutGrid, 
        color: 'bg-teal-500',
        description: 'Definir fase do Kanban',
      },
      { 
        type: 'update_contact', 
        label: 'Atualizar Contato', 
        icon: User, 
        color: 'bg-indigo-500',
        description: 'Modificar dados do contato',
      },
      { 
        type: 'transfer_human', 
        label: 'Transferir Humano', 
        icon: UserPlus, 
        color: 'bg-red-500',
        description: 'Transferir para atendimento humano',
      },
      { 
        type: 'transfer_department', 
        label: 'Transferir Depto', 
        icon: Building, 
        color: 'bg-red-600',
        description: 'Transferir para departamento',
      },
      { 
        type: 'pause_bot', 
        label: 'Pausar Bot', 
        icon: UserPlus, 
        color: 'bg-amber-500',
        description: 'Pausar automação (Human Takeover)',
      },
      { 
        type: 'send_notification', 
        label: 'Notificação', 
        icon: Bell, 
        color: 'bg-amber-500',
        description: 'Enviar notificação para destinatário específico',
      },
      { 
        type: 'resume_bot', 
        label: 'Reativar Bot', 
        icon: Play, 
        color: 'bg-emerald-500',
        description: 'Reativar automação do bot',
      },
    ],
  },
  {
    name: 'CRM',
    icon: Briefcase,
    blocks: [
      { 
        type: 'crm_create_deal', 
        label: 'Criar Negócio', 
        icon: Plus, 
        color: 'bg-violet-500',
        description: 'Criar novo negócio no pipeline CRM',
      },
      { 
        type: 'crm_change_phase', 
        label: 'Alterar Fase', 
        icon: ArrowRightLeft, 
        color: 'bg-violet-600',
        description: 'Mover negócio para outra fase do pipeline',
      },
      { 
        type: 'crm_assign_seller', 
        label: 'Atribuir Vendedor', 
        icon: UserCheck, 
        color: 'bg-violet-400',
        description: 'Atribuir vendedor a um negócio existente',
      },
      { 
        type: 'crm_create_activity', 
        label: 'Criar Atividade', 
        icon: ClipboardList, 
        color: 'bg-violet-500',
        description: 'Registrar nota, ligação, e-mail, reunião, lembrete, WhatsApp ou visita na timeline do negócio',
      },
      { 
        type: 'crm_scheduling_link', 
        label: 'Link Agendamento', 
        icon: CalendarClock, 
        color: 'bg-cyan-500',
        description: 'Gerar link único de agendamento online para o negócio',
      },
    ],
  },
  {
    name: 'Instagram',
    icon: Instagram,
    blocks: [
      { 
        type: 'message_type_check', 
        label: 'Verificar Tipo Msg', 
        icon: MessageSquareMore, 
        color: 'bg-gradient-to-r from-purple-600 to-pink-500',
        description: 'Ramifica baseado na origem (comentário ou DM)',
      },
      { 
        type: 'instagram_message', 
        label: 'Mensagem Instagram', 
        icon: Instagram, 
        color: 'bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400',
        description: 'Enviar mensagem com modo configurável (público/privado/DM)',
      },
    ],
  },
  {
    name: 'Contratos',
    icon: FileSignature,
    blocks: [
      {
        type: 'contract_generate',
        label: 'Gerar Contrato',
        icon: FileSignature,
        color: 'bg-teal-500',
        description: 'Gerar contrato automaticamente a partir de template publicado',
      },
    ],
  },
  {
    // RELEASE: 1.0.0 - 2026-08-29 - Node Conexão (providers do Integration Hub)
    name: 'Integrações',
    icon: Plug,
    blocks: [
      {
        type: 'connection_query',
        label: 'Conexão',
        icon: Plug,
        color: 'bg-sky-500',
        description: 'Consultar um aplicativo conectado e usar o retorno no fluxo',
      },
    ],
  },
  {
    name: 'Financeiro',
    icon: DollarSign,
    blocks: [
      {
        type: 'charge_generate',
        label: 'Gerar Cobrança',
        icon: DollarSign,
        color: 'bg-emerald-500',
        description: 'Gerar cobrança financeira automaticamente',
      },
    ],
  },
  {
    // Seção Tarefas — novo node 'task_create' (RELEASE 1.0.0 - 2026-05-31)
    name: 'Tarefas',
    icon: ListTodo,
    blocks: [
      {
        type: 'task_create',
        label: 'Criar Tarefa',
        icon: ListTodo,
        color: 'bg-amber-500',
        description: 'Criar tarefa automaticamente no módulo Tarefas',
      },
      {
        // RELEASE 1.0.0 - 2026-05-31
        type: 'task_change_phase',
        label: 'Alterar Fase da Tarefa',
        icon: ListTodo,
        color: 'bg-amber-500',
        description: 'Mover uma tarefa existente para outra fase da mesma categoria',
      },
      {
        // RELEASE 1.0.0 - 2026-05-31
        type: 'task_assign',
        label: 'Atribuir Responsável',
        icon: ListTodo,
        color: 'bg-amber-500',
        description: 'Definir ou remover o responsável de uma tarefa existente',
      },
    ],
  },
  {
    name: 'Integrações',
    icon: Webhook,
    blocks: [
      { 
        type: 'webhook', 
        label: 'Webhook', 
        icon: Webhook, 
        color: 'bg-gray-600',
        description: 'Chamar API externa',
      },
    ],
  },
  {
    name: 'Rastreamento',
    icon: BarChart3,
    blocks: [
      { 
        type: 'google_tag_manager', 
        label: 'Google Tag Manager', 
        icon: Tags, 
        color: 'bg-blue-600',
        description: 'Disparar evento no GTM dataLayer',
      },
      { 
        type: 'meta_pixel', 
        label: 'Meta Pixel', 
        icon: Target, 
        color: 'bg-indigo-600',
        description: 'Disparar evento de conversão no Facebook Pixel',
      },
      { 
        type: 'google_analytics', 
        label: 'Google Analytics', 
        icon: BarChart3, 
        color: 'bg-orange-500',
        description: 'Disparar evento no GA4 via gtag',
      },
    ],
  },
];

/**
 * Componente de bloco arrastável
 */
function DraggableBlock({ block }: { block: BlockDefinition }) {
  /**
   * Callback quando usuário inicia arraste do bloco
   */
  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, block.type)}
      className="flex items-center gap-2 p-2 rounded-lg cursor-grab hover:bg-accent 
                 transition-colors border border-border group"
      title={block.description}
    >
      <div className={`p-1.5 rounded ${block.color}`}>
        <block.icon className="h-4 w-4 text-white" />
      </div>
      <span className="text-sm font-medium">{block.label}</span>
    </div>
  );
}

/**
 * Componente principal da sidebar com categorias de blocos
 * @param disabled - Se true, desabilita o arraste de blocos (sem permissão de edição)
 */
export function FlowSidebar({ disabled }: { disabled?: boolean }) {
  return (
    <div className={`w-64 bg-card border-r border-border flex flex-col h-full ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Play className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">Blocos</h3>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Arraste para o canvas
        </p>
      </div>

      {/* Categorias e blocos */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {blockCategories.map((category, idx) => (
            <div key={category.name}>
              {/* Header da categoria */}
              <div className="flex items-center gap-2 mb-2">
                <category.icon className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-medium text-muted-foreground uppercase">
                  {category.name}
                </h4>
              </div>

              {/* Blocos da categoria */}
              <div className="space-y-2">
                {category.blocks.map((block) => (
                  <DraggableBlock key={block.type} block={block} />
                ))}
              </div>

              {/* Separador entre categorias */}
              {idx < blockCategories.length - 1 && (
                <Separator className="mt-4" />
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Footer com ajuda */}
      <div className="p-4 border-t border-border">
        <p className="text-xs text-muted-foreground">
          💡 <span className="font-medium">Dica:</span> Conecte blocos arrastando da saída de um para a entrada de outro
        </p>
      </div>
    </div>
  );
}
